import { useState, useEffect } from 'react';
import { api, SubscriptionInfo, SubscriptionTier } from '../../lib/api';

export function SubscriptionCard() {
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [tiers, setTiers] = useState<SubscriptionTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingTierChange, setPendingTierChange] = useState<'standard' | 'pro' | null>(null);

  // On `?subscription=success`, force a Stripe reconcile before reading the
  // subscription — otherwise we might show stale "free" state if the webhook
  // hasn't arrived yet.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const justCheckedOut = params.get('subscription') === 'success';

    const loadSub = justCheckedOut
      ? api.syncSubscription().catch(() => api.getCurrentSubscription())
      : api.getCurrentSubscription();

    Promise.all([loadSub, api.getSubscriptionTiers()])
      .then(([sub, tierResult]) => {
        setSubscription(sub);
        setTiers(tierResult.tiers);
      })
      .catch(() => setError('Failed to load subscription info'))
      .finally(() => setLoading(false));
  }, []);

  const handleSubscribe = async (tier: 'standard' | 'pro') => {
    setActionLoading(true);
    setError(null);
    try {
      const result = await api.subscribeToTier(tier);
      window.location.href = result.sessionUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start upgrade');
      setActionLoading(false);
    }
  };

  const confirmTierChange = (tier: 'standard' | 'pro') => {
    setPendingTierChange(tier);
  };

  const handleChangeTier = async () => {
    if (!pendingTierChange) return;
    setActionLoading(true);
    setError(null);
    try {
      const updated = await api.changeSubscriptionTier(pendingTierChange);
      // The webhook updates our DB asynchronously. Sync once to converge.
      const synced = await api.syncSubscription().catch(() => updated);
      setSubscription((prev) => (prev ? { ...prev, ...synced } : (synced as SubscriptionInfo)));
      setPendingTierChange(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change plan');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    setActionLoading(true);
    setError(null);
    try {
      const result = await api.cancelSubscription();
      setSubscription((prev) =>
        prev ? { ...prev, cancelAtPeriodEnd: result.cancelAtPeriodEnd, status: 'active' } : prev,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async () => {
    setActionLoading(true);
    setError(null);
    try {
      const result = await api.reactivateSubscription();
      setSubscription((prev) =>
        prev ? { ...prev, cancelAtPeriodEnd: result.cancelAtPeriodEnd } : prev,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reactivate');
    } finally {
      setActionLoading(false);
    }
  };

  const handleManageBilling = async () => {
    try {
      const result = await api.getSubscriptionPortalUrl();
      window.location.href = result.url;
    } catch {
      setError('Failed to open billing portal');
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg shadow-sm border border-border p-6">
        <div className="animate-pulse">
          <div className="h-5 bg-muted rounded w-32 mb-4"></div>
          <div className="h-20 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  if (!subscription) return null;

  const paidTiers = tiers.filter((t) => t.tier !== 'free');
  const isPaid = subscription.tier !== 'free';

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Your Plan</h2>
          <div className="flex items-center gap-2 mt-1">
            <span
              className={`inline-flex px-2.5 py-0.5 text-xs font-semibold rounded-full ${
                isPaid ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
              }`}
            >
              {subscription.tierName}
            </span>
            {subscription.cancelAtPeriodEnd && (
              <span className="inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                Cancels{' '}
                {subscription.currentPeriodEnd
                  ? new Date(subscription.currentPeriodEnd).toLocaleDateString()
                  : 'at period end'}
              </span>
            )}
          </div>
        </div>
        {isPaid && (
          <button
            onClick={handleManageBilling}
            className="text-sm text-primary hover:text-primary/80"
          >
            Manage Billing
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
          {error}
        </div>
      )}

      {isPaid ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Monthly Credits</span>
              <p className="font-medium text-foreground">
                {subscription.monthlyCreditAllocation.toLocaleString()}
              </p>
            </div>
            <div>
              <span className="text-muted-foreground">Storage</span>
              <p className="font-medium text-foreground">{subscription.storageLimitGb} GB</p>
            </div>
            {subscription.currentPeriodEnd && (
              <div>
                <span className="text-muted-foreground">Next Billing</span>
                <p className="font-medium text-foreground">
                  {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                </p>
              </div>
            )}
          </div>

          {/* Other paid tiers — lets the user switch between Standard and Pro
              without creating a second Stripe subscription. */}
          {paidTiers
            .filter((t) => t.tier !== subscription.tier)
            .map((tier) => {
              const isUpgrade = subscription.tier === 'standard' && tier.tier === 'pro';
              return (
                <div key={tier.tier} className="bg-muted/40 border border-border rounded-lg p-4">
                  <div className="flex items-baseline justify-between mb-2">
                    <h3 className="text-sm font-semibold text-foreground">{tier.name}</h3>
                    <span className="text-base font-bold text-foreground">{tier.displayPrice}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">
                    {tier.monthlyCreditAllocation.toLocaleString()} credits / month ·{' '}
                    {tier.storageLimitGb} GB storage
                  </p>
                  <button
                    onClick={() => confirmTierChange(tier.tier as 'standard' | 'pro')}
                    disabled={actionLoading}
                    className="w-full px-4 py-2 text-sm font-medium text-foreground border border-border rounded-lg hover:bg-muted disabled:opacity-50"
                  >
                    {isUpgrade ? `Upgrade to ${tier.name}` : `Switch to ${tier.name}`}
                  </button>
                </div>
              );
            })}

          {subscription.cancelAtPeriodEnd ? (
            <button
              onClick={handleReactivate}
              disabled={actionLoading}
              className="w-full px-4 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50"
            >
              {actionLoading ? 'Processing...' : 'Reactivate Subscription'}
            </button>
          ) : (
            <button
              onClick={handleCancel}
              disabled={actionLoading}
              className="w-full px-4 py-2 text-sm font-medium text-muted-foreground border border-border rounded-lg hover:bg-muted disabled:opacity-50"
            >
              {actionLoading ? 'Processing...' : 'Cancel Subscription'}
            </button>
          )}

          {pendingTierChange && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
              <div className="bg-card border border-border rounded-lg shadow-lg max-w-md w-full p-6">
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {subscription.tier === 'standard' && pendingTierChange === 'pro'
                    ? 'Upgrade to Pro?'
                    : 'Switch to Standard?'}
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {subscription.tier === 'standard' && pendingTierChange === 'pro'
                    ? "You'll be charged a prorated amount immediately and get the Pro monthly credit allocation right away. Your billing date doesn't change."
                    : 'Stripe will credit you for any unused Pro time. Your credit allocation will drop to the Standard amount.'}
                </p>
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => setPendingTierChange(null)}
                    disabled={actionLoading}
                    className="px-4 py-2 text-sm font-medium text-muted-foreground border border-border rounded-lg hover:bg-muted disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleChangeTier}
                    disabled={actionLoading}
                    className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50"
                  >
                    {actionLoading ? 'Processing...' : 'Confirm'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground mb-2">
            Subscribe for monthly credits and a smoother experience.
          </p>
          {paidTiers.map((tier) => (
            <div key={tier.tier} className="bg-primary/5 border border-primary/20 rounded-lg p-4">
              <div className="flex items-baseline justify-between mb-3">
                <h3 className="text-sm font-semibold text-foreground">{tier.name}</h3>
                <span className="text-lg font-bold text-primary">{tier.displayPrice}</span>
              </div>
              <ul className="space-y-1.5 text-sm text-muted-foreground mb-4">
                {tier.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <svg
                      className="w-4 h-4 text-primary flex-shrink-0 mt-0.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => handleSubscribe(tier.tier as 'standard' | 'pro')}
                disabled={actionLoading}
                className="w-full px-4 py-2.5 text-sm font-medium text-primary-foreground bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50"
              >
                {actionLoading ? 'Processing...' : `Subscribe to ${tier.name}`}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
